import { RTKQueryTag } from 'src/common/constants';
import { ApiResponse, PaginatedResponse } from 'src/common/interfaces';
import {
  CreateWorkspaceBody,
  CreateWorkspaceResponse,
  SOCSimTeamsPointsResponse,
  SOCSimTeamsRankResponse,
  UpdateWorkspaceUsersBody,
  WorkspaceResponseBody,
  InviteWorkspaceTeammatesResponse,
  InviteWorkspaceTeammatesBody,
} from 'src/common/interfaces/companies/workspace';
import { apiSlice } from 'src/features/api/api-slice';
import { LeaderboardBase, LeaderboardQuery } from 'src/features/soc-sim/landing-sections/leaderboard/leaderboard.types';

import { WorkspaceKPIData } from './components/workspace-kpi/kpi.types';
import { UpdateWorkspaceDetailsBody } from './components/workspace.types';
import { GetWorkspacePreSignedUrlBody, GetWorkspacePreSignedUrlResponse } from './workspace.types';

const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getWorkspace: builder.query<WorkspaceResponseBody, undefined>({
      query: () => `workspace`,
      providesTags: () => [{ type: RTKQueryTag.Workspace }],
      transformResponse: (response: ApiResponse<WorkspaceResponseBody>) =>
        response.data ?? { hasJoinedWorkspace: false, isWorkspaceCreated: false },
    }),
    getSocSimLeaderboardForWorkspace: builder.query<PaginatedResponse<LeaderboardBase> | undefined, LeaderboardQuery>({
      query: ({ page, limit, sortBy, sortOrder, period }) => ({
        method: 'GET',
        url: 'workspace/soc-sim',
        params: { limit, page, sortBy, sortOrder, period },
      }),
      providesTags: () => [RTKQueryTag.WorkspaceSocSimLeaderboard],
      transformResponse: (response: ApiResponse<PaginatedResponse<LeaderboardBase>>) =>
        response.data && {
          ...response.data,
          docs: response.data.docs.map((doc, i) => ({ ...doc, rank: (response.data?.pagingCounter ?? 1) + i })),
        },
    }),
    getWorkspaceKPI: builder.query<WorkspaceKPIData, void>({
      query: () => 'workspace/dashboard-kpi',
      providesTags: () => [RTKQueryTag.WorkspaceKPI],
      transformResponse: (response: ApiResponse<WorkspaceKPIData>) => response.data as WorkspaceKPIData,
    }),
    joinWorkspace: builder.mutation<ApiResponse, void>({
      query: () => ({ method: 'PATCH', url: `workspace/join` }),
      invalidatesTags: [RTKQueryTag.Workspace],
    }),
    leaveWorkspace: builder.mutation<ApiResponse, void>({
      query: () => ({ method: 'PATCH', url: `workspace/leave` }),
      invalidatesTags: [RTKQueryTag.Workspace],
    }),
    createWorkspace: builder.mutation<ApiResponse<CreateWorkspaceResponse>, CreateWorkspaceBody>({
      query: (body) => ({ method: 'POST', url: 'workspace', body }),
      invalidatesTags: [RTKQueryTag.Workspace],
    }),
    updateWorkspaceDetails: builder.mutation<ApiResponse, UpdateWorkspaceDetailsBody>({
      query: (body) => ({
        url: 'workspace/details',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.Workspace],
    }),
    getWorkspaceLogoPreSignedUrl: builder.query<GetWorkspacePreSignedUrlResponse, GetWorkspacePreSignedUrlBody>({
      query: (params) => ({
        url: 'workspace/logo-upload-url',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<GetWorkspacePreSignedUrlResponse>) =>
        <GetWorkspacePreSignedUrlResponse>response.data,
    }),
    getSOCSimTeamsPoints: builder.query<SOCSimTeamsPointsResponse, void>({
      query: () => 'soc-sim/teams/points',
      providesTags: [RTKQueryTag.SOCSimTeamsPoints],
    }),
    getSOCSimTeamsRank: builder.query<SOCSimTeamsRankResponse, void>({
      query: () => 'soc-sim/teams/rank',
      providesTags: [RTKQueryTag.SOCSimTeamsRank],
    }),
    updateWorkspaceUsers: builder.mutation<ApiResponse, UpdateWorkspaceUsersBody>({
      query: (changes) => ({
        method: 'PATCH',
        url: 'workspace/users',
        body: changes,
      }),
      invalidatesTags: [RTKQueryTag.Workspace],
    }),
    inviteWorkspaceTeammates: builder.mutation<
      ApiResponse<InviteWorkspaceTeammatesResponse>,
      InviteWorkspaceTeammatesBody
    >({
      query: (body) => ({ method: 'POST', url: 'workspace/invite-teammates', body }),
      invalidatesTags: [
        RTKQueryTag.WorkspaceInviteTeammates,
        RTKQueryTag.Workspace,
        RTKQueryTag.WorkspaceSocSimLeaderboard,
      ],
    }),
  }),
});

export const {
  useGetWorkspaceQuery,
  useJoinWorkspaceMutation,
  useCreateWorkspaceMutation,
  useLazyGetWorkspaceLogoPreSignedUrlQuery,
  useUpdateWorkspaceDetailsMutation,
  useUpdateWorkspaceUsersMutation,
  useGetSocSimLeaderboardForWorkspaceQuery,
  useGetSOCSimTeamsPointsQuery,
  useGetSOCSimTeamsRankQuery,
  useGetWorkspaceKPIQuery,
  useInviteWorkspaceTeammatesMutation,
  useLeaveWorkspaceMutation,
  endpoints: WorkspaceEndpoints,
} = extendedApiSlice;
